import { useState, useEffect } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { useWorkspaceSettings } from "@/hooks/useWorkspaceSettings";
import { captureError } from '@/lib/sentry';

const DEFAULT_OPTIONS = [
  { days: 7, label: '1 week' },
  { days: 14, label: '2 weeks' },
  { days: 30, label: '1 month' },
  { days: 60, label: '2 months' },
  { days: 90, label: '3 months' },
  { days: 180, label: '6 months' },
  { days: 360, label: '1 year' },
];

export function SkipOptionsSettings() {
  const [options, setOptions] = useState<Array<{ days: number; label: string }>>(DEFAULT_OPTIONS);
  const [loading, setLoading] = useState(true);
  const { currentWorkspace } = useWorkspaces();
  const { settings, updateSettings, loading: settingsLoading } = useWorkspaceSettings();
  const { toast } = useToast();
  const canModify = currentWorkspace?.role === 'owner' || currentWorkspace?.role === 'admin';

  useEffect(() => {
    if (settings?.skip_options && Array.isArray(settings.skip_options) && settings.skip_options.length > 0) {
      setOptions(settings.skip_options);
    }
    setLoading(false);
  }, [settings]);

  const saveOptions = async (newOptions: typeof options) => {
    if (!currentWorkspace) return;

    try {
      await updateSettings({ skip_options: newOptions });
      setOptions(newOptions);
    } catch (error) {
      console.error('Error updating skip options:', error);
      captureError(error as Error, { feature: 'settings-skip-options-settings-save-options' });
      toast({
        title: "Error",
        description: "Failed to update skip options",
        variant: "destructive",
      });
    }
  };

  const addOption = () => {
    const newOptions = [...options, { days: 30, label: 'New Option' }];
    saveOptions(newOptions);
  };

  const removeOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index);
    saveOptions(newOptions);
  };

  const updateOption = (index: number, field: 'days' | 'label', value: string) => {
    const newOptions = [...options];
    if (field === 'days') {
      newOptions[index].days = parseInt(value) || 0;
    } else {
      newOptions[index].label = value;
    }
    saveOptions(newOptions);
  };

  if (loading || settingsLoading) {
    return <div>Loading skip options...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Skip Options</h2>
        <p className="text-muted-foreground">
          Configure the available options for scheduling next contact
        </p>
      </div>

      <div className="space-y-4">
        {options.map((option, index) => (
          <div key={index} className="flex items-center gap-4">
            <Input
              type="number"
              value={option.days}
              onChange={(e) => updateOption(index, 'days', e.target.value)}
              className="w-24"
              disabled={!canModify}
            />
            <Input
              value={option.label}
              onChange={(e) => updateOption(index, 'label', e.target.value)}
              className="flex-1"
              disabled={!canModify}
            />
            {canModify && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeOption(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}

        {canModify && (
          <Button onClick={addOption}>
            <Plus className="h-4 w-4 mr-2" />
            Add Option
          </Button>
        )}
      </div>
    </div>
  );
}